import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Nav } from 'react-bootstrap';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { getAudienceData } from '../services/api';
import AudienceDemographics from './AudienceDemographics';
import AudienceBrandAndInterests from './AudienceBrandAndInterests';
import AudienceFollowerTypes from './AudienceFollowerTypes';
import { getCountryName } from '../utils/languageUtils';
import SimilarProfiles from './SimilarProfiles';
import ErrorFallback from './ErrorFallback';
const COLORS = ['#680DE4', '#04DB8E', '#E5E5E8'];

const AudienceSection = ({ influencerId, platformAccountType }) => {
  const [genderDistribution, setGenderDistribution] = useState([]);
  const [audienceCountries, setAudienceCountries] = useState([]);
  const [audienceCities, setAudienceCities] = useState([]);
  const [audienceLanguage, setAudienceLanguage] = useState([]);
  const [audienceEthnicity, setAudienceEthnicity] = useState([]);
  const [audienceAge, setAudienceAge] = useState([]);
  const [brandData, setBrandData] = useState([]);
  const [interestData, setInterestData] = useState([]);
  const [followerTypes, setFollowerTypes] = useState([]);
  const [error, setError] = useState(null);
  const [activeLink, setActiveLink] = useState('Countries');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const audienceResponse = await getAudienceData(influencerId, platformAccountType);
        if (audienceResponse.status === 404) {
          setError("Profile not found. Please check the influencer ID.");
        } else {
          setGenderDistribution(audienceResponse.data.gender_distribution.sort((a, b) => b.value - a.value));
          setAudienceCountries(audienceResponse.data.countries.sort((a, b) => b.value - a.value));
          setAudienceCities(audienceResponse.data.cities.sort((a, b) => b.value - a.value));
          setAudienceLanguage(audienceResponse.data.languages.sort((a, b) => b.value - a.value));
          setAudienceEthnicity(audienceResponse.data.ethnicities.sort((a, b) => b.value - a.value));
          setAudienceAge(audienceResponse.data.gender_age_distribution);
          setBrandData(audienceResponse.data.brand_affinity.sort((a, b) => b.value - a.value));
          setInterestData(audienceResponse.data.interests.sort((a, b) => b.value - a.value));
          setFollowerTypes(audienceResponse.data.follower_types.sort((a, b) => b.value - a.value));
          setError(null);
        }
      } catch (error) {
        console.error(error);
        setError("An error occurred while fetching the details.");
      }
    };

    fetchData();
  }, [influencerId, platformAccountType]);

  const renderCustomLegend = () => (
    <ul className="custom-legend gap-2 p-0">
      {genderDistribution.map((entry, index) => (
        <li key={`item-${index}`} style={{ display: 'flex', alignItems: 'center', marginBottom: '5px' }}>
          <span
            style={{
              display: 'inline-block',
              width: '16px',
              height: '16px',
              backgroundColor: COLORS[index % COLORS.length],
              marginRight: '10px',
              borderRadius: '50%',
            }}
          ></span>
          <span style={{ color: '#717680', fontSize: '14px', fontWeight: '400', marginRight: '5px' }}>{entry.gender === 'MALE' ? 'Male' : entry.gender === 'FEMALE' ? 'Female' : 'Others'}</span>
          <span style={{ color: '#0A0D12', fontSize: '14px', fontWeight: '700' }}>({entry.value.toFixed(2)}%)</span>
        </li>
      ))}
    </ul>
  );

  const renderGenderChart = () => {
    const centerLabel = genderDistribution[0];
    return (
      <div style={{ position: 'relative', width: '100%', height: 250 }}>
        <ResponsiveContainer>
          <PieChart>
            <Pie
              data={genderDistribution}
              dataKey="value"
              nameKey="label"
              cx="50%"
              cy="50%"
              innerRadius={76}
              outerRadius={100}
              paddingAngle={1}
              isAnimationActive={false}
            >
              {genderDistribution.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Pie
              data={[{ gender: 'male', value: 100 }]}
              dataKey="value"
              cx="50%"
              cy="50%"
              innerRadius={60}
              outerRadius={80}
              fill="#E9EAEB"
              isAnimationActive={false}
            />
          </PieChart>
        </ResponsiveContainer>
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
          }}
        >
          <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#0A0D12' }}>{centerLabel.label} {centerLabel.value.toFixed(0)}%</div>
          <div style={{ fontSize: '12px', color: '#717680' }}>Top Audience</div>
        </div>
      </div>
    );
  };
  return (
    <div className='audience-section border rounded-0'>
      {error || genderDistribution.length === 0 || audienceCountries.length === 0 ? (
        <ErrorFallback
          title="Audience"
          image={require('../assets/icons/audience-empty.svg').default} // update to match your image path if needed
        />
      ) : (
        <Card className="">
          <Card.Body>
            <div className='info-header-title'>Audience</div>
            <Row>
              <Col md={12} lg={6} className="mt-4">
                <Card.Subtitle as="h6" className="mb-2 sub-heading">Gender distribution</Card.Subtitle>
                <div className="d-flex justify-content-center align-items-center flex-wrap border audience-border-radius py-4">
                  <div className="chart-size">{renderGenderChart()}</div>
                  {renderCustomLegend()}
                </div>
              </Col>
              <Col md={12} lg={6} className="mt-4">
                <Card.Subtitle as="h6" className="mb-3 sub-heading">Audience location</Card.Subtitle>
                <div className='audience-border-radius border p-2'>
                  <Nav variant="underline" className="performance-type-tabs pb-0 mb-3 d-flex">
                    <Nav.Item className="flex-fill text-center">
                      <Nav.Link active={activeLink === 'Countries'} onClick={() => setActiveLink('Countries')} className="text-capitalize m-0">Countries</Nav.Link>
                    </Nav.Item>
                    <Nav.Item className="flex-fill text-center">
                      <Nav.Link active={activeLink === 'Cities'} onClick={() => setActiveLink('Cities')} className="text-capitalize m-0">Cities</Nav.Link>
                    </Nav.Item>
                  </Nav>
                  <Row className="ms-2 me-2 mt-2 overflow-container">
                    {(activeLink === 'Countries' ? audienceCountries : audienceCities).map((item, index) => (
                      <Col key={index} xs={12} sm={12} md={12} lg={12} className="mb-2 pb-2 d-flex align-items-center justify-content-between border-bottom">
                        <div className="circular-label">
                          {activeLink === 'Countries' ? getCountryName(item.code) : item.name}
                        </div>
                        <div className="circular-progress-container me-3">
                          <svg viewBox="0 0 52 52" className="circular-chart" width="56" height="56">
                            <circle
                              className="circle-bg"
                              cx="26"
                              cy="26"
                              r="22"
                              fill="none"
                              stroke="#eee"
                              strokeWidth="18"
                            />
                            <circle
                              className="circle"
                              cx="26"
                              cy="26"
                              r="22"
                              fill="none"
                              stroke="#680DE4"
                              strokeWidth="18"
                              strokeDasharray={`${Math.round((item.value / 100) * 157)}, 157`}
                              strokeLinecap="round"
                              transform="rotate(-90 28 28)"
                            />
                            <text x="26" y="30" textAnchor="middle" className="percentage" fontSize="16px" fill="#0A0D12" fontWeight="bold">
                              {item.value.toFixed(0)}%
                            </text>
                          </svg>
                        </div>
                      </Col>

                    ))}
                  </Row>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>)}

      {error || audienceLanguage.length === 0 || audienceEthnicity.length === 0 || audienceAge.length === 0 || brandData.length === 0 || interestData.length === 0 || followerTypes.length === 0 ?
        (<ErrorFallback
          title="Audience Demographics"
          image={require('../assets/icons/audience-empty1.svg').default} // update to match your image path if needed
        />) : (
          <>
            <AudienceDemographics audienceLanguage={audienceLanguage} audienceEthnicity={audienceEthnicity} audienceAge={audienceAge} />
            <AudienceBrandAndInterests brandData={brandData} interestData={interestData} />
            <Row className=''>
              <Col sm={12} xs={12} md={12} lg={6} className='follower-type-col'> <AudienceFollowerTypes data={followerTypes} /></Col>
              <Col sm={12} xs={12} md={12} lg={6} className='similar-profiles-col'><SimilarProfiles influencerId={influencerId} platformAccountType={platformAccountType} /></Col>
            </Row>
          </>)
      }
    </div>
  );
};

export default AudienceSection;
